import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useSeoMeta } from '@unhead/react';
import { useNavigate, Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';

import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/useToast';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useNostrRepos, type RepositoryEvent } from '@/hooks/useNostrRepos';
import { useAuthor } from '@/hooks/useAuthor';
import { useAuthors, type AuthorData } from '@/hooks/useAuthors';
import { useRepoReactions } from '@/hooks/useRepoReactions';
import { useZaps } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { genUserName } from '@/lib/genUserName';
import { cn, generateVividGradient } from '@/lib/utils';
import { ReactionButton } from '@/components/git/ReactionButton';
import { ShareProjectPublicDialog } from '@/components/ShareProjectPublicDialog';

import {
  Compass,
  GitBranch,
  Copy,
  Check,
  Search,
  User,
  Folder,
  SlidersHorizontal,
  Heart,
  Zap,
  Clock,
  Share2,
} from 'lucide-react';

type SortOption = 'newest' | 'most-liked' | 'most-zapped';

const SORT_OPTIONS: { value: SortOption; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'newest', label: 'Newest', icon: Clock },
  { value: 'most-liked', label: 'Most Liked', icon: Heart },
  { value: 'most-zapped', label: 'Most Zapped', icon: Zap },
];

function RepositoryCard({
  repo,
  index,
  prefetchedAuthor,
  onMetricsLoaded,
}: {
  repo: RepositoryEvent;
  index: number;
  prefetchedAuthor?: AuthorData;
  onMetricsLoaded?: (repoId: string, metrics: { likeCount: number; zapCount: number; zapTotal: number }) => void;
}) {
  const { toast } = useToast();
  const projectsManager = useProjectsManager();
  const [isCloning, setIsCloning] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { webln, activeNWC } = useWallet();

  // Use prefetched author data if available, otherwise fetch individually
  const { data: authorData } = useAuthor(prefetchedAuthor ? undefined : repo.pubkey);
  const resolvedAuthorData = prefetchedAuthor ?? authorData;
  const metadata = resolvedAuthorData?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(repo.pubkey);

  // Fetch reactions for this repo
  const { data: reactions = [] } = useRepoReactions({ repoEventId: repo.id });

  // Fetch zaps for this repo
  const { totalSats, count: zapCount } = useZaps(repo, webln, activeNWC);

  // Report metrics back to parent when they load
  useMemo(() => {
    if (onMetricsLoaded && (reactions.length > 0 || totalSats > 0)) {
      onMetricsLoaded(repo.id, {
        likeCount: reactions.length,
        zapCount: zapCount,
        zapTotal: totalSats,
      });
    }
  }, [reactions.length, totalSats, zapCount, repo.id, onMetricsLoaded]);

  const nameTag = repo.tags.find((tag) => tag[0] === 'name');
  const dTag = repo.tags.find((tag) => tag[0] === 'd');
  const descTag = repo.tags.find((tag) => tag[0] === 'description');
  const cloneTag = repo.tags.find((tag) => tag[0] === 'clone');
  const imageTag = repo.tags.find((tag) => tag[0] === 'image');

  const repoName = nameTag?.[1] || dTag?.[1] || 'Unnamed Repository';
  const projectSlug = dTag?.[1] || '';
  const description = descTag?.[1] || '';
  const cloneUrl = cloneTag?.[1] || (dTag?.[1] ? `nostr://${repo.pubkey}/${dTag[1]}` : '');
  const coverImage = imageTag?.[1];

  const npub = useMemo(() => nip19.npubEncode(repo.pubkey), [repo.pubkey]);
  const projectUrl = projectSlug ? `/${npub}/${projectSlug}` : undefined;
  const fullProjectUrl = projectUrl ? `https://afterhours.diy${projectUrl}` : undefined;

  // Generate a deterministic vivid gradient based on the repo id
  const gradientStyle = useMemo(() => ({
    background: generateVividGradient(repo.id),
  }), [repo.id]);

  const handleClone = useCallback(async () => {
    if (!cloneUrl) return;

    setIsCloning(true);
    try {
      await projectsManager.init();
      const project = await projectsManager.cloneProject(repoName, cloneUrl);

      toast({
        title: 'Repository imported',
        description: `"${project.name}" is ready to edit.`,
      });

      navigate(`/project/${project.id}?build`);
    } catch (error) {
      console.error('Failed to clone repository:', error);
      toast({
        title: 'Failed to import repository',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsCloning(false);
    }
  }, [cloneUrl, navigate, projectsManager, repoName, toast]);

  const handleCopy = async () => {
    if (!cloneUrl) return;
    try {
      await navigator.clipboard.writeText(cloneUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast({
        title: 'Failed to copy',
        description: 'Could not copy repository URL',
        variant: 'destructive',
      });
    }
  };

  const handleOpenProfile = () => {
    navigate(`/${npub}`);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.03 }}
        className="group"
      >
        <div className="relative rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10">
        {/* Clickable header with cover image or gradient */}
        {coverImage ? (
          projectUrl ? (
            <Link to={projectUrl} className="relative aspect-[16/10] block">
              <img src={coverImage} alt={repoName} className="w-full h-full object-cover" />
            </Link>
          ) : (
            <div className="relative aspect-[16/10]">
              <img src={coverImage} alt={repoName} className="w-full h-full object-cover" />
            </div>
          )
        ) : (
          projectUrl ? (
            <Link to={projectUrl} className="relative aspect-[16/10] flex items-center justify-center" style={gradientStyle}>
              <Folder className="h-16 w-16 text-white/50" />
            </Link>
          ) : (
            <div className="relative aspect-[16/10] flex items-center justify-center" style={gradientStyle}>
              <Folder className="h-16 w-16 text-white/50" />
            </div>
          )
        )}

        <div className="relative p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              {projectUrl ? (
                <Link to={projectUrl}>
                  <h3 className="font-semibold text-base md:text-lg tracking-tight truncate group-hover:text-primary transition-colors hover:underline">
                    {repoName}
                  </h3>
                </Link>
              ) : (
                <h3 className="font-semibold text-base md:text-lg tracking-tight truncate group-hover:text-primary transition-colors">
                  {repoName}
                </h3>
              )}
              {description && (
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {description}
                </p>
              )}
            </div>
            <div className="flex-shrink-0 rounded-xl bg-muted/70 p-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          <button
            type="button"
            onClick={handleOpenProfile}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted">
              {metadata?.picture ? (
                <img
                  src={metadata.picture}
                  alt={displayName}
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <User className="h-3.5 w-3.5" />
              )}
            </div>
            <div className="flex flex-col items-start min-w-0">
              <span className="text-xs font-medium truncate max-w-[10rem]">{displayName}</span>
              {metadata?.nip05 && (
                <span className="text-[11px] text-muted-foreground/80 truncate max-w-[10rem]">
                  {metadata.nip05}
                </span>
              )}
            </div>
          </button>

          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              className="flex-1 h-9 rounded-xl text-sm shadow-sm"
              onClick={handleClone}
              disabled={isCloning || !cloneUrl}
            >
              <GitBranch className="h-3.5 w-3.5 mr-2" />
              Clone
            </Button>
            <ReactionButton repoEventId={repo.id} />
            <Button
              size="sm"
              variant="outline"
              className="h-9 w-9 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5"
              onClick={() => setShareDialogOpen(true)}
            >
              <Share2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                'h-9 w-9 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5',
                !copied && 'opacity-60 hover:opacity-100',
                copied && 'border-green-500/60 bg-green-500/10 text-green-600 opacity-100',
              )}
              onClick={handleCopy}
              disabled={!cloneUrl}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>

    {/* Share Dialog */}
    <ShareProjectPublicDialog
      projectName={repoName}
      projectUrl={fullProjectUrl || `https://afterhours.diy/${npub}`}
      liveUrl={undefined}
      open={shareDialogOpen}
      onOpenChange={setShareDialogOpen}
    />
  </>
  );
}

function RepositorySkeleton({ index }: { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, delay: index * 0.03 }}
      className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 overflow-hidden"
    >
      <div className="aspect-[16/10] bg-muted/60 flex items-center justify-center">
        <Folder className="h-16 w-16 text-muted-foreground/30" />
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <Skeleton className="h-8 w-8 rounded-xl" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 flex-1 rounded-xl" />
          <Skeleton className="h-9 w-9 rounded-xl" />
        </div>
      </div>
    </motion.div>
  );
}

export default function Explore() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');

  const { data: repos, isLoading } = useNostrRepos();
  const { webln, activeNWC } = useWallet();

  // Get all unique pubkeys from repos for batch author fetch
  const repoPubkeys = useMemo(() => {
    if (!repos) return [];
    return repos.map((repo) => repo.pubkey);
  }, [repos]);

  // Batch fetch all authors at once for efficient searching
  const { data: authorsMap } = useAuthors(repoPubkeys);

  // Get repo IDs for fetching reactions
  const repoIds = useMemo(() => repos?.map((repo) => repo.id) ?? [], [repos]);

  useSeoMeta({
    title: 'Explore Nostr Git Projects - AfterHours',
    description: 'Discover Nostr-powered git repositories from across the network. Browse decentralized open-source projects built by developers worldwide.',
    ogTitle: 'Explore Nostr Git Projects - AfterHours',
    ogDescription: 'Discover Nostr-powered git repositories from across the network. Browse decentralized open-source projects built by developers worldwide.',
    ogImage: 'https://afterhours.diy/afterhours-og.png',
    ogUrl: 'https://afterhours.diy/explore',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Explore Nostr Git Projects - AfterHours',
    twitterDescription: 'Discover Nostr-powered git repositories from across the network.',
    twitterImage: 'https://afterhours.diy/afterhours-og.png',
    link: [
      { rel: 'canonical', href: 'https://afterhours.diy/explore' },
    ],
  });

  // Create a map of repo IDs to their reaction counts and zap counts
  const [repoMetrics, setRepoMetrics] = useState<Map<string, { likeCount: number; zapCount: number; zapTotal: number }>>(new Map());

  // Initialize metrics map when repos load
  useMemo(() => {
    if (repos) {
      const newMetrics = new Map<string, { likeCount: number; zapCount: number; zapTotal: number }>();
      repos.forEach((repo) => {
        newMetrics.set(repo.id, repoMetrics.get(repo.id) || { likeCount: 0, zapCount: 0, zapTotal: 0 });
      });
      setRepoMetrics(newMetrics);
    }
  }, [repos]);

  // Callback to update metrics when they load from individual cards
  const handleMetricsLoaded = useCallback((repoId: string, metrics: { likeCount: number; zapCount: number; zapTotal: number }) => {
    setRepoMetrics((prev) => {
      const next = new Map(prev);
      next.set(repoId, metrics);
      return next;
    });
  }, []);

  const filteredAndSortedRepos = useMemo(() => {
    if (!repos) return [];

    // First, filter by search query
    const q = search.trim().toLowerCase();
    let filtered = repos;

    if (q) {
      filtered = repos.filter((repo) => {
        const name = repo.tags.find((tag) => tag[0] === 'name')?.[1] ?? '';
        const dTag = repo.tags.find((tag) => tag[0] === 'd')?.[1] ?? '';
        const desc = repo.tags.find((tag) => tag[0] === 'description')?.[1] ?? '';

        // Include author metadata in search
        const authorData = authorsMap?.get(repo.pubkey);
        const authorName = authorData?.metadata?.name ?? '';
        const authorDisplayName = authorData?.metadata?.display_name ?? '';
        const authorNip05 = authorData?.metadata?.nip05 ?? '';

        const haystack = `${name} ${dTag} ${desc} ${authorName} ${authorDisplayName} ${authorNip05}`.toLowerCase();
        return haystack.includes(q);
      });
    }

    // Then, sort based on selected option
    if (sortBy === 'newest') {
      // Sort by created_at descending (newest first) - this is the default
      return [...filtered].sort((a, b) => b.created_at - a.created_at);
    } else if (sortBy === 'most-liked') {
      // Sort by like count descending
      return [...filtered].sort((a, b) => {
        const aMetrics = repoMetrics.get(a.id) || { likeCount: 0, zapCount: 0, zapTotal: 0 };
        const bMetrics = repoMetrics.get(b.id) || { likeCount: 0, zapCount: 0, zapTotal: 0 };
        return bMetrics.likeCount - aMetrics.likeCount;
      });
    } else if (sortBy === 'most-zapped') {
      // Sort by zap total descending
      return [...filtered].sort((a, b) => {
        const aMetrics = repoMetrics.get(a.id) || { likeCount: 0, zapCount: 0, zapTotal: 0 };
        const bMetrics = repoMetrics.get(b.id) || { likeCount: 0, zapCount: 0, zapTotal: 0 };
        return bMetrics.zapTotal - aMetrics.zapTotal;
      });
    }

    return filtered;
  }, [repos, search, authorsMap, sortBy, repoMetrics]);

  return (
    <AppLayout fullWidth>
      <div className="px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              {t('explore', 'Explore Apps')}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl">
              {t(
                'exploreDescription',
                'Browse open-source projects published across Nostr git relays.',
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 w-full max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('searchProjectsOrAuthors', 'Search projects or authors')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 rounded-2xl bg-background/80 border-border/60 focus-visible:ring-primary/30 text-base"
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="default"
                  className="h-12 gap-2 rounded-2xl border-border/60 hover:border-primary/40 hover:bg-primary/5"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {SORT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={cn(
                        'gap-2 cursor-pointer',
                        sortBy === option.value && 'bg-muted'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {option.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.div>

        {isLoading ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <RepositorySkeleton key={i} index={i} />
            ))}
          </div>
        ) : filteredAndSortedRepos.length > 0 ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {filteredAndSortedRepos.map((repo, index) => (
              <RepositoryCard
                key={repo.id}
                repo={repo}
                index={index}
                prefetchedAuthor={authorsMap?.get(repo.pubkey)}
                onMetricsLoaded={handleMetricsLoaded}
              />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="rounded-2xl border-2 border-dashed border-border/60 bg-muted/40 py-16 px-6 text-center"
          >
            <Compass className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
            <p className="font-medium text-muted-foreground mb-1">{t('noProjectsFound', 'No projects found yet')}</p>
            <p className="text-sm text-muted-foreground/80 max-w-md mx-auto">
              {t(
                'noProjectsFoundDescription',
                'Try again in a moment or publish a repository from the Clone page to help seed the network.',
              )}
            </p>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
}
